import { inject, Injectable } from '@angular/core';
import { InfoApiService } from './api/info-api.service';
import { AccessRights } from '../../shared/models/info/access-rights';
import { BehaviorSubject, filter, map, Observable, of, ReplaySubject, shareReplay } from 'rxjs';
import { Policy } from '../../shared/models/info/policy';
import { errorToString } from '../common/common-utils';

@Injectable({
  providedIn: 'root',
})
export class AccessRightsService {
  private initialized$: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  private infoApiService = inject(InfoApiService);
  private defaultAccessRights: AccessRights = new AccessRights(undefined);
  private initialized = false;
  private accessRights: AccessRights = this.defaultAccessRights;
  private accessRights$: Observable<AccessRights> | undefined;

  private policies = new Map<Policy, ReplaySubject<boolean>>(
    (Object.values(Policy) as Policy[]).map((p) => [p, new ReplaySubject<boolean>(1)])
  );

  // needs to be called as soon the user logs in
  public actOnAccessRights(callback: (value: AccessRights) => void): void {
    if (this.initialized) {
      callback(this.accessRights);
      return;
    }
    this.setAccessRights();

    this.initialized$.pipe(filter((val) => val !== undefined)).subscribe(() => {
      callback(this.accessRights);
    });
  }

  // call this after logout
  public clearAccessRights(): void {
    this.accessRights = this.defaultAccessRights;
    this.setPolicyStates(this.accessRights);
    this.initialized = false;
    this.initialized$.next(false);
    this.accessRights$ = undefined;
  }

  public getAccessRights$(): Observable<AccessRights> {
    if (!this.accessRights$) {
      this.accessRights$ = this.infoApiService
        .getAccessRights()
        .pipe(map((ar) => new AccessRights(ar)))
        .pipe(shareReplay(1));
    }
    return this.accessRights$;
  }

  public isPolicySatisfied$(policy: Policy): Observable<boolean> {
    return this.policies.get(policy)?.asObservable() ?? of(false);
  }

  public getCurrentUser$(): Observable<string | undefined> {
    return this.getAccessRights$().pipe(map((ar) => ar.userName || undefined));
  }

  private setAccessRights(): void {
    this.getAccessRights$().subscribe({
      next: (ar) => {
        this.accessRights = ar;
        this.setPolicyStates(this.accessRights);
        this.initialized = true;
        this.initialized$.next(true);
      },
      error: (error) => {
        console.error(errorToString(error));
        this.accessRights = this.defaultAccessRights;
        this.setPolicyStates(this.accessRights);
        this.initialized = false;
        this.initialized$.next(false);
      },
    });
  }

  private setPolicyState(policy: Policy, satisfied: boolean): void {
    if (!this.policies.has(policy)) {
      this.policies.set(policy, new ReplaySubject(1));
    }
    this.policies.get(policy)!.next(satisfied);
  }

  private setPolicyStates(ar: AccessRights): void {
    (Object.values(Policy) as Policy[]).forEach((p) => {
      this.setPolicyState(p, ar.authorized(p));
    });
  }
}
